import { AspectRatio } from '@lumen5/luminary';
import { z } from 'zod';

import { ApiService, NarrativeThreadEnum } from '../../client';
import {
    getDisplayGroupById,
    getSupportedAspectRatios,
} from '../../data/templates/templates-selectors';
import {
    renderSceneDesignsToDataUrls,
    TemplateBundleNotLoadedError,
} from '../rendering/scene-design-render';
import {
    buildStripPlan,
    buildStripPreamble,
    compositeTileStrip,
    normalizeAspectRatio,
    type StripPanel,
} from '../rendering/scene-design-tile-strip';
import type {
    EnhancedToolResult,
    ImageContent,
    TextContent,
    ToolDefinition,
    ToolExecutionContext,
} from '../types';

import { AddVoiceoverSceneTool } from './AddVoiceoverSceneTool';
import { ConversationTool } from './ConversationTool';
import { CreateBlankVideoTool } from './CreateBlankVideoTool';
import { FindTemplatesTool } from './FindTemplatesTool';
import type { ToolConstructorArgs } from './Tool';

export const listSceneDesignSchema = z.object({
    template_id: z
        .string()
        .optional()
        .describe(
            'Optional template ID to list designs from (e.g., "shamrock", "minimal", "modern"). Use this when the user specifies a template name like "from shamrock template" or "in the modern template". If not provided, uses the currently loaded template.',
        ),
    aspect_ratio: z
        .string()
        .optional()
        .describe(
            'Optional aspect ratio (e.g., "landscape", "square", "portrait"). If not provided, defaults to "landscape" or uses the primary aspect ratio of the loaded template.',
        ),
    // Description is set in extendParameters: it names add_voiceover_scene, whose module and
    // this one import each other, so reading AddVoiceoverSceneTool.toolName here (at module
    // load) would hit its temporal dead zone. extendParameters runs lazily at getToolDefinition.
    narrative_thread: z.enum(NarrativeThreadEnum).optional(),
});
export type ListSceneDesignArgs = z.infer<typeof listSceneDesignSchema>;

interface ListSceneDesignConstructorArgs extends ToolConstructorArgs {
    narrativeThread?: NarrativeThreadEnum;
}

export class ListSceneDesignTool extends ConversationTool<
    typeof listSceneDesignSchema,
    ListSceneDesignConstructorArgs
> {
    public static readonly toolName = 'list_scene_designs';

    protected override readonly schema = listSceneDesignSchema;
    protected override readonly toolName = ListSceneDesignTool.toolName;
    protected override readonly toolDescription = `List the available scene designs for a template as rendered preview images. Designs are grouped by display group and returned as tile-strip images (up to 4 designs per strip), each panel labeled with its display name and scene_design_id so you can act on a specific design. Previews require the template to be loaded — when creating a new video, call ${CreateBlankVideoTool.toolName} first, then list the designs.`;

    private narrativeThread: NarrativeThreadEnum;
    public constructor(args: ListSceneDesignConstructorArgs) {
        super(args);
        this.narrativeThread = args.narrativeThread ?? NarrativeThreadEnum.TEXT;
    }

    // narrative_thread's description names add_voiceover_scene, whose module cyclically imports
    // this one; reading AddVoiceoverSceneTool.toolName in the module-level schema would dead-zone.
    // extendParameters runs at getToolDefinition, after all modules have loaded.
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        (parameters.properties.narrative_thread as { description?: string }).description =
            `Which kind of scene designs to list. Use "text" for text-on-media (slide) videos. Use "talking_head" or "voiceover" to include overlay-compatible designs (e.g. lower thirds) when finding a design for ${AddVoiceoverSceneTool.toolName} on a talking-head or voiceover scene group. Defaults to text.`;
        return parameters;
    }

    public override getToolDisplayName(): string {
        return 'Listing scene designs';
    }

    public get statusIcon(): string {
        return 'view_list';
    }

    public async execute(
        args: ListSceneDesignArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const {
            template_id: providedTemplateId,
            aspect_ratio: providedAspectRatio,
            narrative_thread: providedNarrativeThread,
        } = args;

        // Declare variables outside try block for error handling
        let templateId: string | undefined;
        let aspectRatio: AspectRatio = AspectRatio.LANDSCAPE;
        // An explicit narrative_thread arg (e.g. talking_head for overlay designs) wins;
        // otherwise fall back to the tool's configured default (text in the toolbox).
        const narrativeThread = providedNarrativeThread ?? this.narrativeThread;

        try {
            // Get template ID - use provided template_id or fall back to current luminary template
            if (providedTemplateId) {
                templateId = providedTemplateId;
            } else {
                templateId = this.luminary.template.id;
                if (!templateId) {
                    return {
                        result: 'Error: No template loaded and no template_id provided',
                        isSuccess: false,
                    };
                }
            }

            // Get aspect ratio. Priority:
            // 1. Explicitly provided aspect_ratio.
            // 2. The loaded video's aspect ratio — so the previews match the orientation
            //    the user is actually building in. Without this, a vertical video would
            //    get landscape previews.
            // 3. The template's primary supported aspect ratio (when no video is loaded).
            let rawAspectRatio = 'landscape';
            if (providedAspectRatio) {
                rawAspectRatio = providedAspectRatio;
            } else if (this.luminary.aspectRatio) {
                rawAspectRatio = this.luminary.aspectRatio;
            } else if (this.getState) {
                try {
                    const state = this.getState();
                    const supportedAspectRatios = getSupportedAspectRatios(state, templateId);
                    rawAspectRatio = supportedAspectRatios[0] || 'landscape';
                } catch (error) {
                    console.warn(
                        'Could not get supported aspect ratios, using landscape as default:',
                        error,
                    );
                }
            }
            aspectRatio = normalizeAspectRatio(rawAspectRatio);

            // The annotations endpoint is the source of truth for *which* designs are
            // relevant to the narrative thread (it includes overlay-compatible designs
            // for talking_head/voiceover). We only use it for that filtered id set —
            // the annotation text itself is no longer sent to the model.
            const annotationsResponse = await ApiService.sceneDesignsTemplateAnnotationsRetrieve({
                query: {
                    template_id: templateId,
                    aspect_ratio: aspectRatio,
                    narrative_thread: narrativeThread,
                },
            });
            const relevantIds = new Set(
                (annotationsResponse.data?.scene_design_annotations ?? []).map(
                    (item) => item.scene_design_id,
                ),
            );
            if (relevantIds.size === 0) {
                return { result: 'No scene designs found in current template', isSuccess: true };
            }

            // Full design metadata (display_group, display_name, order) for grouping.
            const designsResponse = await ApiService.sceneDesignsList({
                query: { template_id: templateId },
            });
            const designs = (designsResponse.data ?? []).filter((design) =>
                relevantIds.has(design.id),
            );
            if (designs.length === 0) {
                return { result: 'No scene designs found in current template', isSuccess: true };
            }

            const state = this.getState();
            const getGroupName = (groupId: number): string =>
                getDisplayGroupById(state, groupId)?.get('display_name') ?? '';

            const plan = buildStripPlan(designs, aspectRatio, getGroupName);

            const renders = await renderSceneDesignsToDataUrls({
                templateId,
                aspectRatio,
                sceneDesignIds: designs.map((design) => design.id),
                getState: this.getState,
            });
            const renderMap = new Map(renders.map((r) => [r.sceneDesignId, r.dataUrl]));

            const resultContent: (TextContent | ImageContent)[] = [];
            for (const group of plan) {
                const groupDesignCount = group.strips.reduce((n, strip) => n + strip.length, 0);
                resultContent.push({
                    type: 'text',
                    text: `### ${group.groupName} (${groupDesignCount} design${
                        groupDesignCount === 1 ? '' : 's'
                    })`,
                });
                const totalStrips = group.strips.length;
                for (let i = 0; i < group.strips.length; i++) {
                    const strip = group.strips[i];
                    const dataUrls = strip.map((design) => renderMap.get(design.id) ?? null);
                    const panels: StripPanel[] = strip.map((design, index) => ({
                        displayName: design.display_name,
                        sceneDesignId: design.id,
                        rendered: dataUrls[index] != null,
                    }));
                    const stripImage = await compositeTileStrip(dataUrls, aspectRatio);
                    resultContent.push({
                        type: 'text',
                        text: buildStripPreamble(panels, i + 1, totalStrips),
                    });
                    resultContent.push({ type: 'image_url', image_url: { url: stripImage } });
                }
            }

            const result = `Rendered ${designs.length} design${
                designs.length === 1 ? '' : 's'
            } across ${plan.length} group${plan.length === 1 ? '' : 's'} as tile-strip previews.`;

            return { isSuccess: true, result, resultContent };
        } catch (error) {
            if (error instanceof TemplateBundleNotLoadedError) {
                return {
                    result: `Scene design previews can only be rendered for the currently loaded template. Template "${templateId}" is not loaded, so there is nothing to preview. If you are creating a new video, call ${CreateBlankVideoTool.toolName} (with this template) first, then list the designs.`,
                    isSuccess: false,
                };
            }

            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

            // Parse specific error types and provide actionable feedback
            const errorLower = errorMessage.toLowerCase();

            if (errorLower.includes('template does not exist') || errorLower.includes('404')) {
                return {
                    result: `Error: Template "${templateId}" does not exist in the system.

Suggestions:
1. Use the ${FindTemplatesTool.toolName} tool first to find available templates (e.g., ${FindTemplatesTool.toolName}(search_term="modern professional"))
2. Try a different template_id parameter (common templates: "shamrock", "modern", "minimal")
3. If no template_id was provided, make sure a template is loaded in the video

Parameters used: template_id="${templateId}", aspect_ratio="${aspectRatio}", narrative_thread="${narrativeThread}"`,
                    isSuccess: false,
                };
            }

            if (errorLower.includes('aspect_ratio')) {
                return {
                    result: `Error: Invalid aspect_ratio "${aspectRatio}".

Valid aspect ratios: "landscape", "square", "portrait" (or "vertical")

Try calling ${ListSceneDesignTool.toolName} again with a valid aspect_ratio parameter.`,
                    isSuccess: false,
                };
            }

            // Generic error with context
            return {
                result: `Error listing scene designs: ${errorMessage}

Parameters used: template_id="${templateId}", aspect_ratio="${aspectRatio}", narrative_thread="${narrativeThread}"

Suggestion: Try using ${FindTemplatesTool.toolName} first to find available templates, or check if the template is properly loaded.`,
                isSuccess: false,
            };
        }
    }
}
